import SpinnerWrapper from "@arelia-components/ui-atoms/SpinnerWrapper";
import { Box, Button, Card, CardContent, CardHeader, IconButton, TextField, Typography } from "@mui/material";
import { FormikProps } from "formik";
import { useHandleFormActions } from "@arelia-hooks/formikHooks";
import { ChangeEvent } from "react";
import { FormEditActions } from "@arelia-components/form/FormEditActions";
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import RemoveCircleOutlineOutlinedIcon from '@mui/icons-material/RemoveCircleOutlineOutlined';
import { EditForm } from "./CxActivityHistoryContainer";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import MenuSelector from "@arelia-components/ui-atoms/menu-selector/MenuSelector";
import { formatDate } from "@arelia-utils/helpers/dates";

type Props = {
  formik: FormikProps<EditForm>;
  editing: boolean;
  isLoading: boolean;
  setEditing: (editing: boolean) => void;
  addActivity: () => void;
  handleRemoveMember: (i: number) => void;
  handeChange: (index: number, property: string) => (event: ChangeEvent<HTMLInputElement>) => void;
  activities: { name: string; }[] | undefined;
};

export function CxActivityHistoryPresenter({
  editing,
  formik,
  handeChange,
  setEditing,
  isLoading,
  addActivity,
  handleRemoveMember,
  activities
}: Props) {
  const { handleSubmit, handleReset, handleEdit } = useHandleFormActions<EditForm>(setEditing, formik);
  const handleAddActivity = () => addActivity();
  const cxActivities = formik.values.cxActivities;

  return <Card sx={styles.card}>
    <CardHeader
      title="Cx Activities"
      action={<FormEditActions edit={editing} handleEdit={handleEdit} handleReset={handleReset} handleSubmit={handleSubmit} />} />
    <CardContent sx={styles.content}>
      <SpinnerWrapper loading={isLoading}>
        <Box sx={styles.activities}>
          {
            cxActivities?.map((activity, i) =>
              editing
                ? <Box sx={styles.record} key={i}>
                    <Box sx={styles.form}>
                      <DesktopDatePicker
                        label="Date"
                        value={new Date(activity.date)}
                        onChange={(val) => val && handeChange(i, 'date')({ target: { value: val.toISOString() } } as any)}
                        sx={{ width: "150px" }}
                      />
                      <MenuSelector<string>
                        selectedValue={activity.activityName}
                        onClick={activity => handeChange(i, 'activityName')({ target: { value: activity } } as ChangeEvent<HTMLInputElement>)}
                        menuItems={activities?.map(x => ({ label: x.name , value: x.name })) ?? []}
                        sx={{ width: "150px" }}
                      />
                      <TextField
                        value={activity.note ?? ""}
                        onChange={handeChange(i, 'note')}
                        size="small"
                        label="Note"
                        fullWidth
                        style={{ flex: "1" }}
                      />
                    </Box>
                    {i !== cxActivities.length &&
                      <IconButton color="error" onClick={() => handleRemoveMember(i)}>
                        <RemoveCircleOutlineOutlinedIcon />
                      </IconButton>
                    }
                </Box>
                : <Typography key={i} variant="body1">
                  {formatDate(activity.date, "MM/dd/yyyy")} <strong>{activity.activityName}</strong>: {activity.note}
                </Typography>
            )
          }
        </Box>
        {editing && <Button variant="text" startIcon={<AddOutlinedIcon />} onClick={() => handleAddActivity()}>
          Add
        </Button>}
      </SpinnerWrapper>
    </CardContent>
  </Card>
}

const styles = {
  card: {
    gridColumn: "span 8",
    gridRow: "span 8"
  },
  content: {
    overflow: "auto",
    justifyContent: "flex-start",
    gap: "8px"
  },
  activities: {
    paddingTop: "10px",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  record: {
    display: "flex",
    alignItems: "center",
    gap: "16px"
  },
  form: {
    display: "flex",
    gap: "10px",
    flex: "1",
    alignItems: "center",
    width: "200px"
  }
}
